import React from "react";
import MarkdownRenderer from "~/components/utils/MarkdownRenderer";
import RubyText from "~/components/utils/RubyText";
import * as icon from "~/Icons";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import { CustomImageType, feedbackModes } from "~/Commons";

interface ChatMessageLayout2Props {
  role: string;
  content: string;
  onImageLoad?: () => void;
  messageId: string;
  image: ImageBitmap | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  name: string;
  isImgTag: (text: string) => boolean;
  extractImgProps: (text: string) => string;
  animatedText: string;
  t: (key: string) => string;
  isTranscribing: boolean;
  isListening: boolean;
  isFeedbackProcessingRef: React.MutableRefObject<boolean>;
  goodFB: () => void | Promise<void>;
  badFB: () => void | Promise<void>;
  starFB: (star: number) => void | Promise<void>;
  feedbackMode: number;
  fbSelected: string;
  feedbackGood: string;
  feedbackBad: string;
  characterName: string;
  onClickClearSession: () => void;
  isSessionModalOpen: boolean;
  closeSessionModal: () => void;
  clearSession: () => void;
  contentColor?: string;
  customImagesLoaded: CustomImageType[];
}

/**
 * メッセージのクラス名を生成するヘルパー関数
 */
const generateMessageClassName = (role: string, image: ImageBitmap | null): string => {
  const baseClasses = ["message"];

  // ユーザーかアシスタントかによる基本クラス
  baseClasses.push(role === "user" ? "user" : "assistant");

  // アシスタントで画像がある場合の追加クラス
  if (role === "assistant" && image) {
    baseClasses.push("message-with-icon-layout-2");
  }

  return baseClasses.join(" ");
};

const ChatMessageLayout2: React.FC<ChatMessageLayout2Props> = ({
  role,
  content,
  onImageLoad,
  messageId,
  image,
  canvasRef,
  name,
  isImgTag,
  extractImgProps,
  animatedText,
  t,
  isTranscribing,
  isListening,
  isFeedbackProcessingRef,
  goodFB,
  badFB,
  starFB,
  feedbackMode,
  fbSelected,
  feedbackGood,
  feedbackBad,
  // characterName,
  onClickClearSession,
  isSessionModalOpen,
  closeSessionModal,
  clearSession,
  contentColor,
  customImagesLoaded,
  // eslint-disable-next-line complexity
}) => {
  return (
    <div className={generateMessageClassName(role, image)}>
      <div className="message-content">
        {role === "assistant" && image && (
          <>
            <div className="character-icon-layout-2">
              <div className="message-image-layout-2">
                <canvas ref={canvasRef}></canvas>
              </div>
              <div className="character-name-layout-2">
                <RubyText text={name} />
              </div>
            </div>
            {isImgTag(content) ? (
              <div className="qr-code">
                <img src={extractImgProps(content)} alt="二次元コード" onLoad={onImageLoad} />
              </div>
            ) : (
              // テキスト部分をアニメーション表示
              <span className="content" style={contentColor ? { color: contentColor } : undefined}>
                {messageId === "0" ? (
                  <MarkdownRenderer content={content} />
                ) : (
                  <MarkdownRenderer content={animatedText} />
                )}
              </span>
            )}
          </>
        )}
        {role === "assistant" && !image && (
          <>
            {isImgTag(content) ? (
              <div className="qr-code">
                <img src={extractImgProps(content)} alt="二次元コード" onLoad={onImageLoad} />
              </div>
            ) : (
              // テキスト部分をアニメーション表示
              <span className="content" style={contentColor ? { color: contentColor } : undefined}>
                {messageId === "0" ? (
                  <MarkdownRenderer content={content} />
                ) : (
                  <MarkdownRenderer content={animatedText} />
                )}
              </span>
            )}
          </>
        )}
        {role === "user" && (
          <>
            {isImgTag(content) ? (
              <div className="qr-code">
                <img src={extractImgProps(content)} alt="二次元コード" onLoad={onImageLoad} />
              </div>
            ) : (
              // テキスト部分をアニメーション表示
              <span className="content" style={contentColor ? { color: contentColor } : undefined}>
                {messageId === "0" ? (
                  <MarkdownRenderer content={content} />
                ) : (
                  <MarkdownRenderer content={animatedText} />
                )}
              </span>
            )}
          </>
        )}
      </div>
      {/* カスタムイメージがあれば表示 */}
      {role === "assistant" &&
        customImagesLoaded &&
        customImagesLoaded.map((customImage, index) => (
          <div key={`${customImage.key}-${index}`} className="custom-image">
            <img src={customImage.src} alt={customImage.key} />
          </div>
        ))}
      {role === "assistant" && messageId !== "" && messageId !== "0" && (
        <div className="feedback-container">
          <div className="message-review">
            <span className="feedback-question">{t("usabilityReview")}</span>
            {feedbackMode === feedbackModes.detail ? (
              /* 5段階モード: 星評価 */
              <div className="feedback-stars" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isActive =
                    isTranscribing && isListening && !isFeedbackProcessingRef.current;
                  const isFilled = Number(fbSelected) > 0 && star <= Number(fbSelected);
                  return (
                    <span
                      key={star}
                      onClick={isActive ? () => starFB(star) : undefined}
                      data-testid={`fb-star-${star}`}
                      className={`material-symbols-rounded feedback-star ${
                        isFilled ? "feedback-star--filled" : "feedback-star--empty"
                      } ${fbSelected === String(star) ? "selected" : ""} ${
                        star <= 2 ? "bad" : "good"
                      }`}
                      style={{
                        opacity: isActive ? 1 : 0.5,
                        cursor: isActive ? "pointer" : "initial",
                      }}
                    >
                      {"star"}
                    </span>
                  );
                })}
              </div>
            ) : (
              /* シンプルモード: GOOD/BAD */
              <div className="feedback-icons">
                <span
                  onClick={
                    isTranscribing && isListening && !isFeedbackProcessingRef.current
                      ? goodFB
                      : undefined
                  }
                  className={`material-symbols-rounded feedback-thumbs ${fbSelected === feedbackGood ? "selected" : ""}`}
                  style={{
                    opacity:
                      isTranscribing && isListening && !isFeedbackProcessingRef.current ? 1 : 0.5,
                    cursor:
                      isTranscribing && isListening && !isFeedbackProcessingRef.current
                        ? "pointer"
                        : "initial",
                  }}
                >
                  {fbSelected === feedbackGood ? <icon.icons_fb_good /> : "thumb_up"}
                </span>
                <span
                  onClick={
                    isTranscribing && isListening && !isFeedbackProcessingRef.current
                      ? badFB
                      : undefined
                  }
                  className={`material-symbols-rounded feedback-thumbs ${fbSelected === feedbackBad ? "selected" : ""}`}
                  style={{
                    opacity:
                      isTranscribing && isListening && !isFeedbackProcessingRef.current ? 1 : 0.5,
                    cursor:
                      isTranscribing && isListening && !isFeedbackProcessingRef.current
                        ? "pointer"
                        : "initial",
                  }}
                >
                  {fbSelected === feedbackBad ? <icon.icons_fb_bad /> : "thumb_down"}
                </span>
              </div>
            )}
          </div>
          <div>
            <button className="button-1" onClick={() => onClickClearSession()}>
              {t("clearSession")}
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={isSessionModalOpen}
        handleClose={() => closeSessionModal()}
        handleConfirm={() => clearSession()}
        title={t("clearSession")}
        body={t("clearSessionDescription")}
        cancelText={t("cancel")}
        confirmText={t("reset")}
      />
    </div>
  );
};

export default ChatMessageLayout2;
